import type { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { buildObjQuery } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { defaultAxios } from '..';

const taxonomyService = {
  // taxonomy
  async fetchTaxonomyList(workspace_id: string): Promise<TaxonomyT[]> {
    const res = await defaultAxios.get(
      `/workspaces/${workspace_id}/taxonomies`,
    );
    return res.data;
  },
  async createTaxonomyItem(params: TaxonomySetupT): Promise<TaxonomyT> {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.post(`/workspaces/${wa}/taxonomy`, params);
    return res.data;
  },
  async editTaxonomyItem(alias: string, params: TaxonomySetupT) {
    const res = await defaultAxios.patch(`/taxonomies/${alias}`, params);
    return res.data;
  },
  async toggleTaxonomyItemStar(alias: string) {
    const res = await defaultAxios.patch(`/taxonomies/${alias}/starred`);
    return res.data;
  },
  async deleteTaxonomyItem(alias: string) {
    const res = await defaultAxios.delete(`/taxonomies/${alias}`);
    return res.data;
  },
  async fetchTaxonomyItem(alias: string): Promise<TaxonomyT> {
    const res = await defaultAxios.get(`/taxonomies/${alias}`);
    return res.data;
  },

  async fetchTaxonomyTopicList(
    alias: string,
    query?: string,
  ): Promise<TopicRes> {
    const res = await defaultAxios.get(
      `/taxonomies/${alias}/topics${query || ''}`,
    );
    return res.data;
  },

  async fetchTaxonomyFeedbackList(
    alias: string,
    params: {
      query: {
        cursor: string | null;
        sortby?: string;
        order?: Exclude<OrderStatusT, 'unset'>;
      };
      payload: StructuredFilter;
    },
  ): Promise<FetchTopicFeedbackListRes> {
    const { query, payload } = params;
    const queryString = buildObjQuery(query);
    const res = await defaultAxios.post(
      `/taxonomies/${alias}/feedback/list${queryString}`,
      payload,
    );
    return res.data;
  },

  async fetchTaxonomyUnclassifiedFeedbackList(
    alias: string,
    params: {
      query: {
        cursor: string | null;
        snippet: string | null;
        sortby?: string;
        order?: Exclude<OrderStatusT, 'unset'>;
      };
      payload: StructuredFilter;
    },
  ): Promise<FetchTopicFeedbackListRes> {
    const { query, payload } = params;
    const queryString = buildObjQuery(query);
    const res = await defaultAxios.post(
      `/taxonomies/${alias}/feedback/list/unsubclassified${queryString}`,
      payload,
    );
    return res.data;
  },

  async fetchTaxonomyFeedbackCount(alias: string): Promise<FetchCountRes> {
    const res = await defaultAxios.get(`/taxonomies/${alias}/feedback/count`);
    return res.data;
  },

  async fetchTaxonomyUnclassifiedFeedbackCount(
    alias: string,
  ): Promise<FetchCountRes> {
    const res = await defaultAxios.get(
      `/taxonomies/${alias}/feedback/count/unsubclassified`,
    );
    return res.data;
  },

  // Topic

  async fetchTopicFeedbackList(
    alias: string,
    params: {
      query: {
        cursor: string | null;
        snippet: string | null;
        sortby?: string;
        order?: Exclude<OrderStatusT, 'unset'>;
      };
      payload: StructuredFilter;
    },
  ): Promise<FetchTopicFeedbackListRes> {
    const { query, payload } = params;
    const queryString = buildObjQuery(query);
    const res = await defaultAxios.post(
      `/topics2/${alias}/feedback/list${queryString}`,
      payload,
    );
    return res.data;
  },

  async fetchTopicUnclassifiedFeedbackList(
    alias: string,
    params: {
      query: {
        cursor: string | null;
        snippet: string | null;
        sortby?: string;
        order?: Exclude<OrderStatusT, 'unset'>;
      };
      payload: StructuredFilter;
    },
  ): Promise<FetchTopicFeedbackListRes> {
    const { query, payload } = params;
    const queryString = buildObjQuery(query);
    const res = await defaultAxios.post(
      `/topics2/${alias}/feedback/list/unsubclassified${queryString}`,
      payload,
    );
    return res.data;
  },

  async fetchTopicFeedbackCount(alias: string): Promise<FetchCountRes> {
    const res = await defaultAxios.get(`/topics2/${alias}/feedback/count`);
    return res.data;
  },

  async fetchTopicUnclassifiedFeedbackCount(
    alias: string,
  ): Promise<FetchCountRes> {
    const res = await defaultAxios.get(
      `/topics2/${alias}/feedback/count/unsubclassified`,
    );
    return res.data;
  },

  fetchTopicDetail: async (
    topicAlias: string,
  ): Promise<Topic2DetailT<TOPIC_2_TYPE>> => {
    const res = await defaultAxios.get(`/topics2/${topicAlias}`);
    return res.data;
  },

  updateTopicTitle: async (
    topicAlias: string,
    name: string,
  ): Promise<Topic2DetailT<TOPIC_2_TYPE>> => {
    const res = await defaultAxios.patch(`/topics2/${topicAlias}/name`, {
      name,
    });
    return res.data;
  },

  updateTopicDesc: async (
    topicAlias: string,
    desc: string,
  ): Promise<Topic2DetailT<TOPIC_2_TYPE>> => {
    const res = await defaultAxios.patch(`/topics2/${topicAlias}/desc`, {
      desc,
    });
    return res.data;
  },

  async updateTopicDetail<T extends TOPIC_2_TYPE>(
    topicAlias: string,
    params: Topic2UpdateParams<T>,
  ) {
    const res = await defaultAxios.patch(`/topics2/${topicAlias}`, params);
    return res.data;
  },

  async createTopicUnderTaxonomy<T extends TOPIC_2_TYPE>(
    taxonomyAlias: string,
    params: Topic2CreateParams<T>[],
  ) {
    const res = await defaultAxios.post(
      `/taxonomies/${taxonomyAlias}/topic2`,
      params,
    );
    return res.data;
  },

  async createTopicUnderTopic<T extends TOPIC_2_TYPE>(
    topicAlias: string,
    params: Topic2CreateParams<T>[],
  ) {
    const res = await defaultAxios.post(
      `/topics2/${topicAlias}/topic2`,
      params,
    );
    return res.data;
  },

  async deleteTopic(topicAlias: string) {
    const res = await defaultAxios.delete(`/topics2/${topicAlias}`);
    return res.data;
  },

  // feedack coverage

  async fetchTaxonomyCoverage(taxonomyAlias: string): Promise<CoverageData> {
    const res = await defaultAxios.get(
      `/taxonomies/${taxonomyAlias}/feedback/coverage`,
    );
    return res.data;
  },

  async getTopicCoverage(topicAlias: string): Promise<CoverageData> {
    const res = await defaultAxios.get(
      `/topics2/${topicAlias}/feedback/coverage`,
    );
    return res.data;
  },

  async classifyFeedbackToTopic(
    parentTopicAlias: string,
    params: LinkTopicParams,
  ) {
    const res = await defaultAxios.patch(
      `/topics2/${parentTopicAlias}/classify`,
      params,
    );
    return res.data;
  },

  async unclassifyFeedbackToTopic(
    parentTopicAlias: string,
    params: LinkTopicParams,
  ) {
    const res = await defaultAxios.patch(
      `/topics2/${parentTopicAlias}/unclassify`,
      params,
    );
    return res.data;
  },

  async generatePrompt(topicName: string): Promise<AIRulePromptSet> {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.post(`/workspaces/${wa}/topics2/prompt`, {
      name: topicName,
    });

    // const res = await mockupUtils.req(getMockPrompt());
    return res.data;
  },

  async translatePrompt(prompt: string): Promise<AIRulePromptSet> {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.patch(`/workspaces/${wa}/topics2/prompt`, {
      prompt,
    });

    // const res = await mockupUtils.req(getMockPrompt());
    return res.data;
  },

  fetchTopicPreviewFeedback: async <
    T extends TOPIC_2_TYPE,
    K extends CreateTopicTarget,
  >(
    params: GetTopicPreviewParams<T, K>,
  ): Promise<TopicPreviewFeedbackRes> => {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.post(
      `/workspaces/${wa}/topics2/preview`,
      params,
    );

    return res.data;
  },

  async fetchAISuggestionTopic(
    params: AISuggestionParams,
  ): Promise<AISuggestionRes> {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.post(
      `/workspaces/${wa}/suggestions`,
      params,
    );
    // const res = await mockupUtils.req(generateAISuggestions());
    return res.data;
  },

  async excludeFromAnalysis(params: LinkTopicParams) {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.patch(
      `/workspaces/${wa}/filter_items/analysis/block`,
      params,
    );
    return res.data;
  },

  async searchTaxonomyTopic(searchValue?: string): Promise<TopicSearchRes> {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.get(`/workspaces/${wa}/topics2`, {
      params: { name: searchValue },
    });
    return res.data;
  },

  async requestExportTaxonomyBlock(
    taxonomyAlias: string,
    params: ExportTaxonomyBlockParams,
  ) {
    const res = await defaultAxios.post(
      `/taxonomies/${taxonomyAlias}/export`,
      params,
    );
    return res.data;
  },

  async fetchTaxonomyTopicName(
    topicAliases: string[],
  ): Promise<AliasNamePair[]> {
    const res = await defaultAxios.post(`/topics2/name`, {
      aliases: topicAliases,
    });
    return res.data;
  },

  async reclassifyFeedbackInTopic(topicAlias: string) {
    const res = await defaultAxios.post(`/topics2/${topicAlias}/reclassify`);
    return res.data;
  },

  async reclassifyFeedbackInTaxonomy(taxonomyAlias: string) {
    const res = await defaultAxios.post(
      `/taxonomies/${taxonomyAlias}/reclassify`,
    );
    return res.data;
  },
};

export default taxonomyService;
